import {
  ComponentDescriptor,
  getRendererFromDescriptor,
  useField,
  usePage,
} from '@kizi/comparator-core';
import { BaseDivProps, Button, Div, Heading, HeadingProps, P } from '@kizi/core-components';
import React, { useEffect, useState } from 'react';
import {
  TabViewWizard,
  TabViewWizardProps,
} from '../../../src/components/TabViewWizard';
import { InnerTabViewWizard } from '../../../src/components/wrappers/TabViewWizard/ApplyPageWizard';
import { Desktop } from '../../utils/breakpoint';

import { Window } from '../Window';

export interface ModalWithStepsProps
  extends Omit<TabViewWizardProps, 'children'> {
  buttonLabel: string;
  title: string;
  isOpen: boolean;
  handleClick: () => void;
  infoMessage: undefined | string;
  withTabs?: boolean;
  buttonVariant?: string;
  modalContainerDivProps?: BaseDivProps;
  headingProps?: HeadingProps;
}

const InnerModalSteps = ({ steps, wizardOptions, tabs, withTabs = true }) => (
  <TabViewWizard
    display="flex"
    flexDirection="column"
    steps={steps}
    wizardOptions={wizardOptions}
    tabs={tabs}
    tabProps={(selected, { disabled }) => ({
      variant: selected ? 'lightTabViewTabSelected' : 'lightTabViewTab',
      css: {
        cursor: selected || disabled ? 'text' : 'pointer',
      },
    })}
    isScrollable={true}
  >
    {(Tabs, Content) => (
      <InnerTabViewWizard
        Tabs={Tabs}
        Content={Content}
        variant="lightTabViewTabsContainerForModal"
        withTabs={withTabs}
      />
    )}
  </TabViewWizard>
);

export const ModalWithSteps: React.FC<ModalWithStepsProps> = ({
  buttonLabel,
  title,
  steps,
  tabs,
  wizardOptions,
  isOpen,
  infoMessage,
  handleClick,
  withTabs,
  buttonVariant="secondarySmall",
  modalContainerDivProps,
  headingProps
}) => {
  return (
    <>
      <Div>
        <Button
          onClick={handleClick}
          variant={buttonVariant}
          display="flex"
          alignItems="center"
        >
          {buttonLabel}
        </Button>
        {infoMessage ? (
          <P variant="footnoteRegular" mt={3} color="second.0">
            {infoMessage}
          </P>
        ) : null}
      </Div>

      <Window
        onClose={handleClick}
        modalProps={{
          show: isOpen,
          children: (
            <>
              <Desktop>
                <Heading
                  variant="H2"
                  as="h2"
                  color="heading"
                  mt={0}
                  mb={0}
                  textAlign="center"
                  {...headingProps}
                >
                  {title}
                </Heading>
              </Desktop>
              <InnerModalSteps
                steps={steps}
                wizardOptions={wizardOptions}
                tabs={tabs}
                withTabs={withTabs}
              />
            </>
          ),
          modalContainerDivProps
        }}
        show={isOpen}
        title={title}
        type="modal"
      >
        <InnerModalSteps
          steps={steps}
          wizardOptions={wizardOptions}
          tabs={tabs}
          withTabs={withTabs}
        />
      </Window>
    </>
  );
};

export interface ModalWithStepsWrapperProps
  extends Pick<
    ModalWithStepsProps,
    'buttonLabel' | 'title' | 'wizardOptions' | 'withTabs'
  > {
  tabs: { id?: number | string; label: string; content: ComponentDescriptor }[];
  wizardStepsFieldName: string;
  filtersFieldName: string;
  tabsToFilter?: string;
  fieldName: string;
  headingProps?: HeadingProps;
}

export const ModalWithStepsWrapper: React.FC<ModalWithStepsWrapperProps> = ({
  wizardStepsFieldName,
  filtersFieldName,
  tabsToFilter,
  tabs,
  fieldName,
  withTabs,
  headingProps,
  ...modalWithStepsProps
}) => {
  const { state, setState } = usePage();

  const wizardField = useField(wizardStepsFieldName);
  const filtersField = useField(filtersFieldName);
  const field = useField(fieldName);
  const tabsToFilterField = useField(tabsToFilter ?? '');

  if (!wizardField || !filtersField || !setState || !field) return null;

  let _tabsToFilter: any[] = [];
  const steps = JSON.parse(wizardField?.value as string);
  const filters = JSON.parse(filtersField?.value as string);

  if (tabsToFilterField)
    _tabsToFilter = JSON.parse(tabsToFilterField?.value as string);
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState<undefined | string>(undefined);

  const onSubmit = (newFilters, modalSteps) => {
    filtersField.set({
      value: JSON.stringify(newFilters),
    });
    wizardField.set({
      value: JSON.stringify(modalSteps),
    });
    setShow(false);
  };

  const onCancel = () => {
    setShow(false);
  };

  let filteredTabs = tabs.filter((tab) => {
    return _tabsToFilter.length > 0 ? _tabsToFilter.includes(tab.id) : tab;
  });

  useEffect(() => {
    setState({
      ...state,
      filters: filters,
      onSubmit: onSubmit,
      onCancel: onCancel,
      steps: steps,
    });
  }, []);

  const handleClick = () => {
    if (field.active === false) {
      setMessage(
        'Vul je postcode en huisnummer in om de keuzehulp te gebruiken.'
      );
    } else {
      setMessage(undefined);
      setShow(!show);
    }
  };
  
  return (
    <ModalWithSteps
      {...modalWithStepsProps}
      headingProps={headingProps}
      wizardOptions={{
        ...modalWithStepsProps.wizardOptions,
      }}
      tabs={filteredTabs.map((tab, i) => {
        return {
          label: `${i + 1}. ${tab.label}`,
          content: getRendererFromDescriptor(tab.content),
        };
      })}
      steps={steps}
      isOpen={show}
      handleClick={handleClick}
      infoMessage={message}
      withTabs={withTabs}
    />
  );
};
